import type { CustomizationSelection } from '@/features/customization/domain/models/CustomizationSelection';
import type { PdpEddProductSpecs } from '../domain/models/ResolvedExpectedDeliveryDate';
import type { EarlyDeliveryItem } from '../domain/models/EarlyDeliveryItem';
import { formatEddDate } from './eddFormatting';

export interface ActiveSelectionSnapshot {
  customizationId: number;
  metal: string;
  goldPurity: string | null;
  diamondStoneType: string | null;
  solitaireCarat: number | null;
  solitaireClarity: string | null;
  solitaireColor: string | null;
  size: string;
}

export function buildActiveSelectionKey(snapshot: ActiveSelectionSnapshot): string {
  return [
    snapshot.customizationId,
    snapshot.metal,
    snapshot.goldPurity ?? '',
    snapshot.diamondStoneType ?? '',
    snapshot.solitaireCarat ?? '',
    snapshot.solitaireClarity ?? '',
    snapshot.solitaireColor ?? '',
    snapshot.size,
  ].join('|');
}

export function formatEarlyDeliveryMetalLabel(
  item: Pick<EarlyDeliveryItem, 'metalPurity' | 'metalType'>
): string {
  let label = item.metalPurity;
  if (item.metalType) {
    label += item.metalType === 'Gold' ? ' Yellow Gold' : ` ${item.metalType}`;
  }
  return label;
}

export function formatEarlyDeliveryDiamondLabel(
  item: Pick<EarlyDeliveryItem, 'diamondClarity' | 'diamondColor'>
): string | null {
  if (!item.diamondClarity || !item.diamondColor) return null;
  return `${item.diamondClarity}${item.diamondColor} Diamonds`;
}

export function formatEarlyDeliverySolitaireLabel(
  item: Pick<EarlyDeliveryItem, 'solitaireCarat' | 'solitaireClarity' | 'solitaireColor'>
): string | null {
  if (!item.solitaireCarat || !item.solitaireClarity || !item.solitaireColor) return null;
  return `${item.solitaireCarat} ${item.solitaireClarity}${item.solitaireColor} Solitaire`;
}

export function formatEarlyDeliveryByLabel(expectedDeliveryDate: string): string {
  return `Delivery by: ${formatEddDate(expectedDeliveryDate)}`;
}

export function earlyDeliveryItemToSelection(item: EarlyDeliveryItem): CustomizationSelection {
  let metalColor = '';
  if (item.metalColor === 'Yellow') metalColor = 'Gold';
  else if (item.metalColor === 'White') metalColor = 'White Gold';
  else if (item.metalColor === 'Rose') metalColor = 'Rose Gold';

  let productSize = item.size.size;
  if (productSize === '00') productSize = '';

  const diamondType =
    item.diamondClarity && item.diamondColor ? `${item.diamondClarity}${item.diamondColor}` : '';

  let metalKaratage = item.metalPurity;
  if (item.metalType === 'Platinum') {
    metalKaratage = '';
    metalColor = 'Platinum';
  }

  return {
    metal_color: metalColor || undefined,
    metal_karatage: metalKaratage || undefined,
    diamond_type: diamondType || undefined,
    solitaire_carat: item.solitaireCarat ?? undefined,
    solitaire_clarity: item.solitaireClarity ?? undefined,
    solitaire_color: item.solitaireColor ?? undefined,
    size: productSize || undefined,
  };
}

export function buildEddSpecsFromEarlyDeliveryItem(
  item: EarlyDeliveryItem,
  fallbackDefaultSize: string | null
): PdpEddProductSpecs {
  let metal = item.metalType;
  if (item.metalType === 'Platinum') {
    metal = 'Platinum';
  } else if (item.metalColor === 'White') {
    metal = 'White Gold';
  } else if (item.metalColor === 'Rose') {
    metal = 'Rose Gold';
  } else if (item.metalColor === 'Yellow' || item.metalType === 'Gold') {
    metal = 'Gold';
  }

  const parsedSolitaireCarat = item.solitaireCarat ? Number.parseFloat(item.solitaireCarat) : null;

  return {
    metal,
    goldPurity:
      item.metalType === 'Platinum' ? null : item.metalPurity.replace(/kt$/i, '') || null,
    diamondStoneType:
      item.diamondClarity && item.diamondColor
        ? `${item.diamondClarity}${item.diamondColor}`
        : null,
    solitaireCarat: parsedSolitaireCarat != null && Number.isFinite(parsedSolitaireCarat)
      ? parsedSolitaireCarat
      : null,
    solitaireClarity: item.solitaireClarity,
    solitaireColor: item.solitaireColor,
    defaultSize: item.size.size !== '00' ? item.size.size : fallbackDefaultSize,
  };
}
